import { motion } from "framer-motion";
import { Sparkles, Code, Cpu } from "lucide-react";

const tags = [
  "Product Manager",
  "Technical Solution",
  "AI & IoT",
  "Software",
  "Hardware",
  "Smart Energy",
];

const About = () => {
  return (
    <section id="about" data-testid="about-section" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg-fine opacity-30 pointer-events-none" />
      <div className="absolute -top-32 right-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
              /// About
            </div>
            <h2
              className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white"
              style={{ fontFamily: "Outfit" }}
              data-testid="about-title"
            >
              Menjembatani kebutuhan bisnis dengan{" "}
              <span className="text-gradient-aurora">solusi teknis yang dapat dieksekusi.</span>
            </h2>

            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="about-tags">
              {tags.map((t, i) => (
                <motion.span
                  key={t}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  className="px-3.5 py-1.5 rounded-full text-xs border border-blue-400/20 bg-blue-500/[0.06] text-blue-200/90 hover:bg-blue-500/[0.12] hover:border-blue-400/40 transition-colors font-mono-display"
                  data-testid={`about-tag-${i}`}
                >
                  {t}
                </motion.span>
              ))}
            </div>

            {/* Signature block */}
            <div className="mt-10 rounded-2xl p-6 glass-elevated corners max-w-md">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-mono-display mb-2">
                principle.txt
              </div>
              <p className="text-slate-200 italic text-[15px] leading-relaxed" style={{ fontFamily: "Outfit" }}>
                "Solusi yang baik bukan yang paling kompleks, tapi yang paling{" "}
                <span className="text-gradient-aurora not-italic">realistis untuk dieksekusi.</span>"
              </p>
              <div className="mt-3 text-xs text-slate-500 font-mono-display">— Joneten Saputra</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-5"
          >
            <p className="text-lg text-slate-200/95 leading-relaxed">
              Joneten Saputra membantu proses pengembangan produk teknologi dari{" "}
              <span className="text-blue-300">ide</span>, perencanaan fitur, arsitektur solusi,
              implementasi software dan hardware, hingga delivery project.
            </p>
            <p className="text-base text-slate-400 leading-relaxed">
              Joneten Saputra adalah seorang <span className="text-slate-200">Product Manager</span> dan{" "}
              <span className="text-slate-200">Technical Solution Specialist</span> dengan pengalaman panjang
              dalam pengembangan produk teknologi, integrasi sistem, IoT, AI, software, hardware, serta
              manajemen proyek digital. Sejak 2012, Joneten terbiasa menghubungkan kebutuhan bisnis,
              kebutuhan pengguna, dan keputusan teknis sehingga menjadi solusi yang{" "}
              <span className="text-slate-200">realistis, scalable, dan dapat digunakan.</span>
            </p>

            <div className="pt-2 grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl p-5 glass-elevated corners group">
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="w-4 h-4 text-blue-300" strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-blue-400 font-mono-display">
                    approach
                  </span>
                </div>
                <div className="text-white font-medium text-lg" style={{ fontFamily: "Outfit" }}>
                  Realistis &amp; Scalable
                </div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                  Solusi yang dapat dieksekusi dan tumbuh seiring kebutuhan bisnis.
                </p>
              </div>
              <div className="rounded-2xl p-5 glass-elevated corners group">
                <div className="flex items-center gap-2 mb-3">
                  <Code className="w-4 h-4 text-cyan-300" strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-cyan-300 font-mono-display">
                    focus
                  </span>
                </div>
                <div className="text-white font-medium text-lg" style={{ fontFamily: "Outfit" }}>
                  End-to-End Delivery
                </div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                  Dari konsep, arsitektur, hingga implementasi dan delivery project.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
